import { patientsApi } from '../patientApi';
import {
  CancelSubscriptionAutoRenewRequestProps,
  DeactivateWithRefundRequestProps,
  DeactivateWithRefundResponse,
  DowngradeSubscriptionRequestProps,
  DowngradeSubscriptionResponse,
  PauseSubscriptionRequestProps,
  PreviewChangeSubscriptionData,
  PreviewChangeSubscriptionRequestProps,
  ResumeSubscriptionRequestProps
} from './patientSubscription.types';

export const patientSubscriptionApi = patientsApi.injectEndpoints({
  endpoints: (builder) => ({
    cancelSubscriptionAutoRenew: builder.mutation<
      { message: string },
      CancelSubscriptionAutoRenewRequestProps
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Subscription'
      ],
      query: ({ cancelReasonCode, note, patientId, programIds }) => ({
        body: { cancelReasonCode, note, programIds },
        method: 'PATCH',
        url: `/patients/${patientId}/cancel-subscription-auto-renew`
      })
    }),
    changeSubscription: builder.mutation<
      { message: string },
      {
        couponCode?: string;
        currentProgramId?: string;
        patientId: string;
        planId: string;
        planPricePointId: string;
      }
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        { id: 'LIST', type: 'Tags' },
        'Billing'
      ],
      query: ({ couponCode, currentProgramId, patientId, planId, planPricePointId }) => ({
        body: { couponCode, currentProgramId, planId, planPricePointId },
        method: 'PATCH',
        url: `/patients/${patientId}/change-subscription`
      })
    }),
    deactivateWithRefund: builder.mutation<
      DeactivateWithRefundResponse,
      DeactivateWithRefundRequestProps
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        { id: 'LIST', type: 'Tags' }
      ],
      query: ({
        approvingSupervisorName,
        cancelReasonCode,
        note,
        patientId,
        programIds,
        refundAmounts
      }) => ({
        body: {
          ...(approvingSupervisorName === undefined ? {} : { approvingSupervisorName }),
          cancelReasonCode,
          note,
          ...(programIds && { programIds }),
          refundAmounts
        },
        method: 'PATCH',
        url: `/patients/${patientId}/deactivate-with-refund`
      })
    }),
    downgradeSubscription: builder.mutation<
      DowngradeSubscriptionResponse,
      DowngradeSubscriptionRequestProps
    >({
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          setTimeout(() => {
            dispatch(
              patientsApi.util.invalidateTags([
                { id: props.patientId, type: 'Patient' },
                { id: 'LIST', type: 'Tags' }
              ])
            );
          }, 10000);
        } catch {
          // The caller handles the failure through `unwrap()`. Rethrowing here would surface every
          // expected rejection, such as a missing supervisor name, as an unhandled promise rejection.
        }
      },
      query: ({
        approvingSupervisorName,
        cancelReasonCode,
        currentProgramId,
        currentProgramIds,
        note,
        patientId,
        refundAmounts,
        target
      }) => ({
        body: {
          ...(approvingSupervisorName === undefined ? {} : { approvingSupervisorName }),
          cancelReasonCode,
          ...(currentProgramIds ? { currentProgramIds } : { currentProgramId }),
          note,
          refundAmounts,
          target
        },
        method: 'PATCH',
        url: `/patients/${patientId}/downgrade-subscription`
      })
    }),
    getPreviewChangeSubscription: builder.query<
      PreviewChangeSubscriptionData,
      PreviewChangeSubscriptionRequestProps
    >({
      query: ({ couponCode, currentProgramId, patientId, planId, planPricePointId }) => ({
        body: { couponCode, currentProgramId, planId, planPricePointId },
        method: 'PATCH',
        url: `/patients/${patientId}/preview-change-subscription`
      }),
      transformResponse: (response: { data: PreviewChangeSubscriptionData }) => response.data
    }),
    pauseSubscription: builder.mutation<{ message: string }, PauseSubscriptionRequestProps>({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Subscription'
      ],
      query: ({ currentProgramId, patientId, pauseDuration }) => ({
        body: { currentProgramId, pauseDuration },
        method: 'PATCH',
        url: `/patients/${patientId}/pause-subscription`
      })
    }),
    resumeSubscription: builder.mutation<{ message: string }, ResumeSubscriptionRequestProps>({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Subscription'
      ],
      query: ({ currentProgramId, patientId }) => ({
        body: { currentProgramId },
        method: 'POST',
        url: `/patients/${patientId}/resume-subscription`
      })
    })
  })
});

export const {
  useCancelSubscriptionAutoRenewMutation,
  useChangeSubscriptionMutation,
  useDeactivateWithRefundMutation,
  useDowngradeSubscriptionMutation,
  useLazyGetPreviewChangeSubscriptionQuery,
  usePauseSubscriptionMutation,
  useResumeSubscriptionMutation
} = patientSubscriptionApi;
